// -----------------------------------------------------------------------
// <copyright file="gethelp.api.ts" company="Microsoft">
//     copyright (c) Microsoft. All rights reserved.
// </copyright>
// -----------------------------------------------------------------------

import { httpUtils } from '../../../../web/common/apis/httpUtils';
import { appContext } from '../../../../web/models/getHelp/appContext';
import { GetHelpDiagnosticRequest } from '../../../../web/models/getHelp/GetHelpDiagnosticRequest';
import { GetHelpDiagnosticResponse } from '../../../../web/models/getHelp/GetHelpDiagnosticResponse';
import { GetHelpLoggingRequest, LogLevel, TagId } from '../../../../web/models/getHelp/GetHelpLoggingRequest';
import { isGetHelp } from '../../../utilities';

/**
 * Service that exports GetHelp Web Api.
 * Each public method is 1:1 mapped to the API's Controller methods.
 * @class GetHelpService
 */
class GetHelpService {
  private shouldLog = isGetHelp();

  /**
   * Log telemetry event through our backend pipeline by calling its api endpoint.
   * @param {GetHelpLoggingRequest} eventMessage the payload of the event
   */
  public async logEventAsync(eventMessage: GetHelpLoggingRequest): Promise<void> {
    const url = `${appContext.environment.baseUrl}/api/v1/apps/gethelp/logging`;
    try {
      await httpUtils.post(url, eventMessage, {
        'Content-Type': 'application/json',
        'appId': appContext.appId,
        'locale': appContext.locale,
        'sessionId': appContext.sessionId,
        'cv': this.increaseCv(),
      });
    } catch (error) {
      // Do nothing if the logging endpoint is down.
    }
  }

  /**
   * Get Diagnostic sessionId and autohealId results from backend
   * @param {string} problemKey diagnostic package problem key
   * @param {string} problemValue diagnostic package problem value
   * @returns {Promise<GetHelpDiagnosticResponse>} The object from backend with Diagnostic sessionId and autohealId results
   * @throws Throw errors when input argument is not valid or non-success http status from fetch api.
   */
  public async getDiagnosticSessionAsync(problemKey: string, problemValue: string): Promise<GetHelpDiagnosticResponse> {
    // Throw errors if argument is not valid
    if (!problemKey) {
      throw Error(`Empty problemKey.`);
    }
    if (!problemValue) {
      throw Error(`Empty problemValue.`);
    }

    const url = `${appContext.environment.baseUrl}/api/v1/apps/gethelp/diagnosticsession`;
    const request: GetHelpDiagnosticRequest = {
      ProblemKey: problemKey,
      ProblemValue: problemValue,
    };

    try {
      return await httpUtils.post<GetHelpDiagnosticResponse>(url, request);
    } catch (error) {
      if (!!error?.status || !!error?.code) {
        if (error.status === 429 || error?.code === '429') {
          this.logDiagnosticSessionApiThrottlingError(error, problemKey, problemValue);
        }
      } else {
        this.logDiagnosticSessionApiError(error, problemKey, problemValue);
      }
      return null;
    }
  }

  private increaseCv(): string {
    if (!!appContext.correlationVector) {
      const cv = appContext.correlationVector;
      const pos = cv.lastIndexOf('.');
      if (pos > 0) {
        const baseCv = cv.substring(0, pos);
        const iteration = Number(cv.substring(pos + 1)) + 1;
        appContext.correlationVector = `${baseCv}.${iteration}`;
      }
    }
    return appContext.correlationVector;
  }

  private logDiagnosticSessionApiThrottlingError = async (error: any, problemKey: string, problemValue: string) => {
    this.shouldLog && await getHelpService.logEventAsync({
      TagId: TagId.DiagnosticSessionApiThrottlingError,
      LogLevel: LogLevel.Error,
      Message: `Get diagnostic sessionId request failed due to throttling`,
      Adhoc0: appContext.locale,
      Adhoc1: error.status ?? '',
      Adhoc2: error.message ?? '',
      Adhoc3: problemKey,
      Adhoc4: problemValue,
    });
  };

  private logDiagnosticSessionApiError = async (error: any, problemKey: string, problemValue: string) => {
    this.shouldLog && await getHelpService.logEventAsync({
      TagId: TagId.DiagnosticSessionApiError,
      LogLevel: LogLevel.Error,
      Message: `Get diagnostic sessionId request failed`,
      Adhoc0: appContext.locale,
      Adhoc1: error.status ?? '',
      Adhoc2: error.message ?? '',
      Adhoc3: problemKey,
      Adhoc4: problemValue,
    });
  };
}

export const getHelpService = new GetHelpService();
